import "./TemplateRequirementSelector.less";

import { Spin } from "antd";
import arrayMove from "array-move";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { withRouter, RouteComponentProps } from "react-router";

import { SectionTemplateApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import { ComponentTemplate } from "lib/data/model";
import { SectionTemplate } from "lib/data/model";
import { MultipleItemRequirement, Requirement, RequirementItem, SingleItemRequirement } from "lib/data/model";
import { ComponentNames } from "lib/data/model/dictionaries/ComponentNames";
import { IconType } from "lib/data/model/dictionaries/IconTypes";
import { Requiredness } from "lib/data/model/enumerations";
import { StateService } from "lib/services";

import { TemplateCard } from "components/general";
import { actionTypes } from "components/general/TemplateCard/ActionTypes";
import { NewRequirement } from "../../TemplateHierarchy/components/NewRequirement";
import { SortableContainer } from "./SortableContainer";
import { SortableItem } from "./SortableItem";

interface ISectionTemplateSelectorProps extends IBaseProps, RouteComponentProps<any> {
}

type modalTypes = "newSingleItem" | "newMultiItem" | "editMultiItem" | "hidden";

interface ISectionTemplateSelectorState {
	modalSelected: modalTypes;
	multipleItemRequirement: MultipleItemRequirement<ComponentTemplate> | undefined;
	newSections: [];
	waitingForUpdate: boolean;
}



export class SectionTemplateSelectorPreRouter extends React.Component<ISectionTemplateSelectorProps, ISectionTemplateSelectorState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private sectionTemplateItems: Array<RequirementItem<SectionTemplate>> = [];
	private sectionTemplate?: SectionTemplate;
	private requestTimeoutId?: number;

	constructor(props: ISectionTemplateSelectorProps) {

		super(props);

		this.state = {
			modalSelected: "hidden",
			newSections: [],
			multipleItemRequirement: undefined,
			waitingForUpdate: false
		};
	}


	public async componentWillMount() {

		// Grab all section templates at the root level
		const sectionTemplate = await SectionTemplateApi.getDirectorySectionTemplateRequirements();

		// Null check
		if (sectionTemplate != null) {

			this.sectionTemplate = sectionTemplate;

			// check that it has requirements
			if (sectionTemplate.requirements.length) {

				const sectionTemplateRequirementItems = this.loadSectionTemplateItems(sectionTemplate);

				// use the initialOrder item on the requirementItems to order the
				this.sectionTemplateItems = sectionTemplateRequirementItems.sort((sectionTemplateA, sectionTemplateB) => {

					return sectionTemplateA.initialOrder - sectionTemplateB.initialOrder;
				});
			}

			this.forceUpdate();
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		if (this.state.waitingForUpdate) {

			return <Spin />;
		}
		else {

			return (
				<SortableContainer onSortEnd={this.onTemplateDrop} axis="xy" className="template-requirement-selector" >
					{/* <NewSingleItemRequirement className="non-sortable-item" newSingleItemClick={this.newSingleItemRequirementClick} /> */}
					<NewRequirement className="non-sortable-item" newSingleItemClick={this.newSingleItemRequirementClick} newMultipleItemClick={this.newSingleItemRequirementClick} />
					{this.renderTemplateData()}
				</SortableContainer>
			);
		}
	}

	private loadSectionTemplateItems = (sectionTemplate: SectionTemplate): Array<RequirementItem<SectionTemplate>> => {

		// declare a Requirement Item array that will allow us to order all the section templates(initil order is stored on the requirement item)
		const sectionTemplateRequirementItems: Array<RequirementItem<SectionTemplate>> = [];

		// Map over all the requirements
		sectionTemplate.requirements.map((requirment) => {

			// If it is a SingleItem Requirement
			if (requirment.$type === SingleItemRequirement.$type.ComponentTemplate) {

				// create a local variable as a single item requirement
				const singleItemRequirement = requirment as SingleItemRequirement<ComponentTemplate>;

				// Make sure the component type is a section template
				if (singleItemRequirement.item.item.$type === SectionTemplate.$type) {

					// push that section template requirement item to our list
					singleItemRequirement.item.owningRequirement = singleItemRequirement;
					sectionTemplateRequirementItems.push(singleItemRequirement.item as RequirementItem<SectionTemplate>);
				}
			}
			// If it is a SingleItem Requirement
			else if (requirment.$type === MultipleItemRequirement.$type.ComponentTemplate) {

				// create a multiple item requirement local variable
				const multipleItemRequirement = requirment as MultipleItemRequirement<ComponentTemplate>;

				// map over all the requirement items
				multipleItemRequirement.items.map((item) => {

					// Make sure the component type is a section template
					if (item.item.$type === SectionTemplate.$type) {

						// push that section template requirement item to our list
						item.owningRequirement = multipleItemRequirement;
						sectionTemplateRequirementItems.push(item as RequirementItem<SectionTemplate>);
					}
				});

			}
		});

		return sectionTemplateRequirementItems;
	}

	private hardSectionTemplateUpdate = async (callback?: Function) => {

		let sectionTemplate: SectionTemplate | undefined;

		StateService.setCancelableState(() => this.setState({ waitingForUpdate: true }), this.apiCancellationToken);

		if (this.sectionTemplate) {

			if (this.sectionTemplate.id) {

				// set the templateRequirementList to remove circular dependencies (cannot make a request where a resource has circular dependencies)
				sectionTemplate = Object.assign(cloneDeep(this.sectionTemplate), { templateRequirementList: [] });
				sectionTemplate = await SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, sectionTemplate);

				if (sectionTemplate) {

					// reload the sectionTemplateItems
					this.sectionTemplateItems = this.loadSectionTemplateItems(sectionTemplate);
				}
			}
		}

		StateService.setCancelableState(() => this.setState({ waitingForUpdate: false }), this.apiCancellationToken);

		if (callback) {

			callback();
		}

		this.sectionTemplate = sectionTemplate;

		this.forceUpdate();
	}

	private softSectionTemplateUpdate = async (callback?: Function) => {

		if (this.requestTimeoutId) {

			window.clearTimeout(this.requestTimeoutId);
			this.requestTimeoutId = undefined;
		}

		if (this.sectionTemplate) {

			if (this.sectionTemplate.id !== undefined) {

				if (callback) {

					callback();
				}

				this.forceUpdate();

				this.requestTimeoutId = window.setTimeout(
					async () => {

						if (this.sectionTemplate) {

							if (this.sectionTemplate.id) {

								// set the templateRequirementList to remove circular dependencies (cannot make a request where a resource has circular dependencies)
								const sectionTemplate = Object.assign(cloneDeep(this.sectionTemplate), { templateRequirementList: [] });
								await SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, sectionTemplate);
							}
						}
					}, 1000
				);
			}
		}
	}

	private onSectionTemplateSelect = (id: number): void => {

		this.props.history.push(`/admin/templateHierarchy/${id}`);
	}

	private actionClickHandler = (command: actionTypes, index?: number) => {

		switch (command) {

			case "delete":

				if (index !== undefined) {

					if (this.sectionTemplate) {

						// grab the requirement that we want to delete
						const deleteRequirement: Requirement = this.sectionTemplateItems[index].owningRequirement;
						// const deleteRequirement: Requirement = this.sectionTemplate.requirements[index];

						// Delete from sectionTemplateItems list
						this.sectionTemplateItems.splice(index, 1);

						// filter out the owning requirement id from the sectionTemplate
						this.sectionTemplate.requirements = this.sectionTemplate.requirements.filter((requirement) => requirement.id !== deleteRequirement.id);

						this.softSectionTemplateUpdate();

					}
				}

				break;

			case "expand":

				if (index != null && this.sectionTemplate) {

					const sectionTemplateItem = this.sectionTemplateItems[index];

					if (sectionTemplateItem.item.id) {

						this.onSectionTemplateSelect(sectionTemplateItem.item.id)
					}
				}

				break;

			default:

				break;
		}
	}

	private updateSectionTitle = (newTitle: string, callback: Function | undefined, key: number) => {

		this.sectionTemplateItems[key].item.name = newTitle;

		if (callback !== undefined) {

			this.softSectionTemplateUpdate(callback);
		}
	}

	private renderTemplateData = () => {

		// map out all the items
		return (
			this.sectionTemplateItems.map((sectionTemplateItem, key) => {

				return (
					<SortableItem key={`item-${key}`} index={key}>
						<TemplateCard
							canEditTitle={true}
							updateTitle={(newTitle: string, callback?: Function) => this.updateSectionTitle(newTitle, callback, key)}
							onCheckboxclick={() => { }}
							key={sectionTemplateItem.id || undefined}
							requirement={undefined}
							id={sectionTemplateItem.item.id || undefined}
							actionbuttons={["delete", "expand"]}
							actionButtonClick={(command: actionTypes) => { this.actionClickHandler(command, key) }}
							showCheckboxes={false}
							title={sectionTemplateItem.item.name}
							iconType={IconType.get(sectionTemplateItem.item.$type)}
							templateType={ComponentNames[sectionTemplateItem.item.$type]}
						/>
					</SortableItem>
				);
			})
		);
	}

	private newSingleItemRequirementClick = async () => {

		if (this.sectionTemplate) {

			// Create a new SectionTemplate
			const newSectionTemplate: SectionTemplate = new SectionTemplate();
			newSectionTemplate.areRequirementsEnforced = true;
			newSectionTemplate.canReorderChildren = true;
			newSectionTemplate.name = "New Section Template";
			newSectionTemplate.requirements = [];

			// Create a new requirement Item
			const requirementItem = new RequirementItem<ComponentTemplate>("ComponentTemplate") as RequirementItem<SectionTemplate>;
			requirementItem.initialOrder = this.sectionTemplateItems.length + 1;
			requirementItem.item = newSectionTemplate;

			// Create a new requirement
			const requirement = new SingleItemRequirement<ComponentTemplate>("ComponentTemplate") as SingleItemRequirement<SectionTemplate>;
			requirement.isRepeatable = false;
			requirement.requiredness = Requiredness.OPTIONAL;
			requirement.item = requirementItem;

			// create circualar reference
			requirementItem.owningRequirement = requirement;

			// Push requirement item to requirements of section template
			this.sectionTemplate.requirements.push(requirement);

			// Push requirement item to section template items
			this.sectionTemplateItems.push(requirementItem);

			// Force the update
			this.hardSectionTemplateUpdate();
		}
	}

	private onTemplateDrop = async ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {

		// if the section template is avalible and the old and new index are not the same
		if (this.sectionTemplate && oldIndex !== newIndex) {

			// Adjust placement in Template Reuirements
			this.sectionTemplateItems = arrayMove(this.sectionTemplateItems, oldIndex, newIndex);

			// Assign new sort order on the item itself(modifies this.sectionTemplate.requirements[0].item.initialOrder)
			this.sectionTemplateItems.forEach((item, index) => {

				item.initialOrder = index;
			});

			this.forceUpdate();

			// send off changes
			if (this.sectionTemplate.id) {

				SectionTemplateApi.updateSectionTemplate(this.sectionTemplate.id, this.sectionTemplate);
			}

			// update all changes on the screen with this.sectionTemplate
		}
	}
}

export const SectionTemplateSelector = withRouter(SectionTemplateSelectorPreRouter);
